import React, { useEffect, useMemo, useRef, useState } from "react";
import { Pause, Play } from "lucide-react";
import { API } from "../api";

const BAR_COUNT = 56;

export function WaveformPlayer() {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(false);

  const bars = useMemo(() => {
    const arr = [];
    let seed = 13;
    for (let i = 0; i < BAR_COUNT; i++) {
      seed = (seed * 9301 + 49297) % 233280;
      const r = seed / 233280;
      arr.push(20 + r * 80 * (0.5 + 0.5 * Math.sin((i / BAR_COUNT) * Math.PI)));
    }
    return arr;
  }, []);

  const toggle = async () => {
    const a = audioRef.current;
    if (!a) return;
    try {
      if (playing) {
        a.pause();
      } else {
        await a.play();
      }
    } catch {
      setError(true);
    }
  };

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onPlay = () => { setPlaying(true); setError(false); };
    const onPause = () => setPlaying(false);
    const onTime = () => setProgress(a.duration ? a.currentTime / a.duration : 0);
    const onErr = () => setError(true);
    a.addEventListener("play", onPlay);
    a.addEventListener("pause", onPause);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("error", onErr);
    return () => {
      a.removeEventListener("play", onPlay);
      a.removeEventListener("pause", onPause);
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("error", onErr);
    };
  }, []);

  return (
    <div className="glass-panel rounded-2xl p-5" data-testid="waveform-player">
      <audio ref={audioRef} src={`${API}/audio/sample`} preload="none" />
      <div className="flex items-center gap-4">
        <button
          data-testid="waveform-play-button"
          onClick={toggle}
          className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-cyan-500 to-teal-500 text-slate-950 shadow-lg shadow-cyan-500/40 transition-transform hover:scale-105 active:scale-95"
          aria-label={playing ? "Pauza" : "Odtwórz próbkę"}
        >
          {playing ? <Pause className="h-6 w-6" /> : <Play className="ml-0.5 h-6 w-6" />}
        </button>
        <div className="flex-1">
          <div className={`flex h-16 items-center gap-[3px] ${playing ? "wave-playing" : ""}`}>
            {bars.map((h, i) => {
              const active = i / BAR_COUNT <= progress;
              return (
                <div
                  key={i}
                  className={`wave-bar w-full rounded-full ${active ? "bg-gradient-to-t from-cyan-500 to-teal-300" : "bg-slate-700"}`}
                  style={{ height: `${h}%`, animationDelay: `${(i % 12) * 0.08}s` }}
                />
              );
            })}
          </div>
          <div className="mt-2 flex items-center justify-between text-[11px] text-slate-400">
            <span>{error ? "Próbka jest generowana — spróbuj za chwilę" : "Próbka audiobooka · lektor AI · PL"}</span>
            <span>{Math.round(progress * 100)}%</span>
          </div>
        </div>
      </div>
    </div>
  );
}
